import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { MUSIC_ENABLED, MUSIC_HEADER_ENABLED } from "../config.js";

// The audio is on with the whole music feature or with the header's button
// alone (config.js).
const AUDIO_ON = MUSIC_ENABLED || MUSIC_HEADER_ENABLED;

const AudioCtx = createContext(null);

const AUDIO_TRACKS = [
  "/audio/hustle.m4a",
  "/audio/hangon.m4a",
  "/audio/thenyouweregone.m4a",
];

function shuffleQueue(avoidFirst) {
  const indices = AUDIO_TRACKS.map((_, i) => i);
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  if (avoidFirst != null && indices[0] === avoidFirst && indices.length > 1) {
    [indices[0], indices[1]] = [indices[1], indices[0]];
  }
  return indices;
}

export function AudioProvider({ children }) {
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const queueRef = useRef([]);
  const queuePosRef = useRef(0);
  const [playing, setPlaying] = useState(false);

  const ensureGraph = useCallback(() => {
    if (ctxRef.current) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    const source = ctx.createMediaElementSource(audioRef.current);
    source.connect(analyser);
    analyser.connect(ctx.destination);
    ctxRef.current = ctx;
    analyserRef.current = analyser;
    sourceRef.current = source;
  }, []);

  const loadCurrent = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    const idx = queueRef.current[queuePosRef.current];
    const src = AUDIO_TRACKS[idx];
    if (!el.src.endsWith(src)) {
      el.src = src;
    }
  }, []);

  const advance = useCallback(async () => {
    queuePosRef.current += 1;
    if (queuePosRef.current >= queueRef.current.length) {
      const lastIdx = queueRef.current[queueRef.current.length - 1];
      queueRef.current = shuffleQueue(lastIdx);
      queuePosRef.current = 0;
    }
    loadCurrent();
    try {
      await audioRef.current.play();
    } catch {}
  }, [loadCurrent]);

  const toggle = useCallback(async () => {
    const el = audioRef.current;
    if (!el || !AUDIO_ON) return;
    ensureGraph();
    if (ctxRef.current.state === "suspended") {
      await ctxRef.current.resume();
    }
    if (el.paused) {
      if (queueRef.current.length === 0) {
        queueRef.current = shuffleQueue();
        queuePosRef.current = 0;
        loadCurrent();
      }
      await el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  }, [ensureGraph, loadCurrent]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.volume = 0.25;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      advance();
    };
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
    };
  }, [advance]);

  const value = { playing, toggle, analyserRef };

  return (
    <AudioCtx.Provider value={value}>
      {/* Feature off: no src, so nothing is fetched. On, still nothing until
          the first press (preload none): a track is 2-3 MB, and most visits
          never play one. */}
      <audio ref={audioRef} src={AUDIO_ON ? AUDIO_TRACKS[0] : undefined} preload="none" />
      {children}
    </AudioCtx.Provider>
  );
}

export function useAudio() {
  const ctx = useContext(AudioCtx);
  if (!ctx) throw new Error("useAudio must be used inside <AudioProvider>");
  return ctx;
}
